'use client'

import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useEffect } from 'react'
import { usePathname, redirect } from 'next/navigation'
import { useGlobalStore } from '@/store/provider'
import { deleteCookie } from 'cookies-next/client'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { ArrowRightEndOnRectangleIcon } from '@heroicons/react/24/outline'

import Link from 'next/link'
import { Avatar, MenuLink, LinkButton, Divider } from '@/components'
import { pages } from '@/settings'
import { myCruzApi } from '@/api'

type MenuProps = {
  name: string
  icon?: React.ReactNode
  href: string
  count?: number
  onClick: () => void
}

// Header My Menu
export function HeaderMyMenu() {
  const pathname = usePathname()
  const { user, setUser, refreshMyProfile, setRefreshMyProfile, setToast } = useGlobalStore((state) => state)

  useEffect(() => {
    if (refreshMyProfile) {
      setRefreshMyProfile(false)
      getMyProfile()
    }
  }, [refreshMyProfile])

  async function getMyProfile() {
    const d = (await myCruzApi.getProfile())?.data
    if (d) {
      setUser(d, null)
    }
  }

  function MyMenuItem({ name, href, count, icon, onClick }: MenuProps) {
    return (
      <Link hrefLang='en' key={href} href={href} className='menu-item' onClick={onClick}>
        <span className='flex items-center gap-1.5'>
          {icon}
          {name}
        </span>
        {
          (!_.isUndefined(count)) &&
          <span className='nav-count'>{count}</span>
        }
      </Link>
    )
  }

  function signOut() {
    setUser({}, null)
    deleteCookie('access_token')
    deleteCookie('refresh_token')
    if (_.includes(pathname, '/my')) {
      redirect(pages.home.href)
    } else {
      setToast({ type: 'success', message: 'Successfully signed out' })
    }
  }

  if (_.isEmpty(user)) {
    return (
      <>
        <LinkButton variant='secondary' href={pages.signIn.href} label={pages.signIn.name} />
        <MenuLink href={pages.joinCruz.href} className='truncate'>
          Join Cruz<sup className='text-[7px]'>TM</sup>
        </MenuLink>
      </>
    )
  }

  return (
    <Popover as='div' className='relative h-full'>
      {({ close }) =>
        <>
          <PopoverButton className='menu-button font-medium dark:font-normal'>
            <Avatar className='size-12 md:size-10' photo_url={user?.photo_url} name={`${user?.first_name} ${user?.last_name}`} />
            <ChevronDownIcon className='size-5 ml-1.5 -mr-1' />
          </PopoverButton>
          <PopoverPanel anchor='bottom end' transition={true} modal={false} className='menu-items py-2'>
            <div className='text-center truncate pt-4 pb-3 space-y-1 dark:text-zinc-200'>
              <div className='font-medium text-base/6 truncate'>
                {
                  _.isEmpty(user?.first_name)
                    ? user?.email
                    : `${user?.first_name} ${user?.last_name}`
                }
              </div>
              {
                !_.isEmpty(user?.location) &&
                <div className='truncate font-light text-xs'>
                  {user?.location?.city}, {user?.location?.state_abbr} {user?.location?.zip_code}
                </div>
              }
              <div className='truncate font-light text-xs'>
                Joined {dayjs(user?.date_create).fromNow()}
              </div>
            </div>
            <Divider className='pb-4' />
            <MyMenuItem {...pages.myCruz} onClick={close} />
            <MyMenuItem {...pages.mySavedSearch} count={user?.statistic?.saved_search || 0} onClick={close} />
            <MyMenuItem {...pages.mySavedVehicle} count={user?.statistic?.saved_vehicle || 0} onClick={close} />
            <MyMenuItem {...pages.myInquiries} count={user?.statistic?.inquiry_count || 0} onClick={close} />
            <Divider className='py-2' />
            <MyMenuItem {...pages.myVehicles} count={user?.statistic?.vehicle?.all || 0} onClick={close} />
            <MyMenuItem {...pages.postVehicle} onClick={close} />
            <Divider className='py-2' />
            <MyMenuItem {...pages.mySpotted} count={user?.statistic?.spotted_count || 0} onClick={close} />
            <MyMenuItem {...pages.postSpotted} onClick={close} />
            <Divider className='py-2' />
            <MyMenuItem {...pages.myProfile} onClick={close} />
            <MyMenuItem {...pages.myPassword} onClick={close} />
            <Divider className='py-2' />
            <button className='menu-item w-full rounded-b' onClick={() => {
              close(); signOut();
            }}>
              <span className='flex items-center gap-2 w-full'>
                <ArrowRightEndOnRectangleIcon className='w-5' />
                Sign Out
              </span>
            </button>
          </PopoverPanel>
        </>
      }
    </Popover >
  )

}